import { bilingual, type Lang, LOCALE } from "./i18n";
import type { DirectorModel, VideoChoices, Voice } from "./types";
import VOICE_CATALOG from "./voices.json";

type CatalogText = {
  media: Record<MediaKind, { label: string; unit: string }>;
  models: Record<DirectorModel, { note: string }>;
};
/**
 * What customers read about the catalog. MEDIA_CATALOG keeps its own English
 * labels and units because the Director reads them; keep the two in step.
 */
export const CATALOG_TEXT = bilingual<CatalogText>({
  en: {
    media: {
      speech: { label: "Voice", unit: "per 1,000 characters" },
      image: { label: "Images", unit: "per image" },
      music: { label: "Music", unit: "per 30-second track" },
      video: { label: "AI footage", unit: "per 4-second clip" },
    },
    models: {
      foco: { note: "Most capable" },
      lite: { note: "Fewer credits" },
    },
  },
  es: {
    media: {
      speech: { label: "Voz", unit: "por 1000 caracteres" },
      image: { label: "Imágenes", unit: "por imagen" },
      music: { label: "Música", unit: "por pista de 30 segundos" },
      video: { label: "Clips con IA", unit: "por clip de 4 segundos" },
    },
    models: {
      foco: { note: "El más capaz" },
      lite: { note: "Menos créditos" },
    },
  },
});

// Product policy, deliberately narrower than the upstream discovery catalog.
// Refresh upstream capabilities without silently expanding what customers can buy.
export const CATALOG_VERSION = "2026-10-06.1";
export const MEDIA_CATALOG = {
  speech: {
    label: "Voice",
    description: "A voice that fits your story.",
    model: VOICE_CATALOG.model,
    name: "Gemini 3.8 Flash TTS",
    // Only for a voice cloned from the customer's own reference recording.
    alternative: "bytedance-seed/seed-audio-1-0",
    alternativeName: "Seed Audio",
    unit: "per 1,000 characters",
    credits: 3,
  },
  image: {
    label: "Images",
    description: "Original visuals, grounded in your product.",
    model: "google/gemini-3.1-flash-image",
    name: "Gemini Flash Image",
    unit: "per image",
    credits: 6,
  },
  music: {
    label: "Music",
    description: "An original score for the moment.",
    model: "google/lyria-3-pro-preview",
    name: "Lyria 3 Pro",
    unit: "per 30-second track",
    credits: 6,
  },
  video: {
    label: "AI footage",
    description: "New scenes generated from your idea.",
    model: "google/veo-3.1-lite",
    name: "Veo 3.1 Lite",
    unit: "per 4-second clip",
    credits: 9,
  },
} as const;
export type MediaKind = keyof typeof MEDIA_CATALOG;
// Included songs, synced into every workspace. An original song is opt-in per video.
export const MUSIC_LIBRARY = "/workspace/music";
export const creditNumber = (n: number, lang: Lang = "en") =>
  new Intl.NumberFormat(LOCALE[lang], {
    maximumFractionDigits: n < 10 ? 2 : 1,
  }).format(n);

// The Director's models. Customers only ever see the plan names. Rates are the
// provider's price plus a quarter at 60 credits a dollar (a credit is 1⅔¢ of
// it); cache rates keep its ratios. Plans and top-ups sell a credit for 1.6 to
// 2.3 euro cents.
export const INFERENCE_CATALOG = {
  maxOutputTokens: 32000,
  models: {
    foco: {
      name: "Foco",
      note: "Most capable",
      model: "anthropic/claude-opus-5.5",
      claudeCode: "claude-opus-5-5",
      inputCreditsPerMillion: 300,
      cacheWriteCreditsPerMillion: 375,
      cacheReadCreditsPerMillion: 15,
      outputCreditsPerMillion: 1500,
    },
    lite: {
      name: "Foco Lite",
      note: "Fewer credits",
      model: "anthropic/claude-sonnet-5.5",
      claudeCode: "claude-sonnet-5-5",
      inputCreditsPerMillion: 150,
      cacheWriteCreditsPerMillion: 187.5,
      cacheReadCreditsPerMillion: 15,
      outputCreditsPerMillion: 750,
    },
  },
} as const;
export const DIRECTOR_MODELS = Object.keys(INFERENCE_CATALOG.models) as [
  DirectorModel,
  ...DirectorModel[],
];

// Monthly plans; prices exclude VAT. Used to the last credit they keep a
// margin (about 36, 30 and 23 %), and unused plan credits end with the month.
// An account's first plan payment adds `welcome` credits for that month, so
// learning the studio doesn't eat into the plan; that month is about at cost.
export const PLANS = {
  starter: { name: "Starter", price: 29, credits: 1500, welcome: 750 },
  pro: { name: "Pro", price: 79, credits: 4500, welcome: 1500 },
  studio: { name: "Studio", price: 199, credits: 12500, welcome: 2500 },
} as const;
export type PlanId = keyof typeof PLANS;
export const PLAN_IDS = Object.keys(PLANS) as [PlanId, ...PlanId[]];
export const TOPUP = { price: 35, credits: 1500, months: 12 } as const;
/**
 * Señal, the TikTok course whose purchase brings Foco credits, as its own site
 * sells it (biblia-site `site.config.json`): the launch price lasts to the end
 * of its last day in Madrid.
 */
export const SENAL = {
  price: 49,
  credits: 1500,
  launch: { price: 29, until: "2026-10-31" },
} as const;
/** Señal's price on a day, with the launch's last day while it lasts. */
export function senalPrice(at = new Date()) {
  const today = at.toLocaleDateString("en-CA", { timeZone: "Europe/Madrid" });
  return today <= SENAL.launch.until
    ? { price: SENAL.launch.price, until: SENAL.launch.until as string }
    : { price: SENAL.price, until: null };
}
/**
 * Credits for a video made in one go, as plans quote it: 150 for Foco (real
 * videos averaged about 155 and needed no changes) and a third of that for
 * Foco Lite. Counts round down, to a ten from 20 up, so they stay round and
 * never overstate (Starter 10, Pro 30, Studio 80). Refresh them as real
 * videos accumulate.
 */
export const VIDEO_CREDITS: Record<DirectorModel, number> = {
  foco: 150,
  lite: 50,
};
export function videosFor(credits: number, model: DirectorModel) {
  const videos = Math.floor(credits / VIDEO_CREDITS[model]);
  return videos < 20 ? videos : videos - (videos % 10);
}

/** A new video's choices; a product's next video keeps its format and narrator. */
export const DEFAULT_CHOICES: VideoChoices = {
  settings: {
    length: "Let the director decide",
    language: "Let the director decide",
    direction: "",
  },
  // The Director uses a clip where filmed footage serves the video, up to five clips.
  footage: { use: "director", limit: 45 },
  music: "library",
  voice: null,
  voiceLabel: false,
  model: "foco",
};

// The narrators customers choose from: hand-picked voices of the speech model.
export const VOICES = VOICE_CATALOG.voices as Voice[];
export const VOICE_IDS = VOICES.map((v) => v.id) as [string, ...string[]];
// Ranked delivery directions per narration language and voice gender.
export const VOICE_STYLES: Record<
  string,
  Record<Voice["gender"], string>
> = VOICE_CATALOG.styles;
